import { useCallback, useEffect, useState } from 'react';
import type { StepId } from './goLive.js';
import { STEP_ORDER } from './goLive.js';

/**
 * WS6 onboarding — RESUMABLE progress (brief: "Resumable; persist step progress"). The authoritative
 * "done" state for go-live is real salon data (goLive.ts reads the live salon/staff/services), but
 * the wizard also remembers WHERE the operator was — the current step, and which steps they have
 * walked through — so closing the tab and coming back lands them where they left off rather than at
 * step one. That cursor is purely local UI state, so it lives in localStorage keyed per salon (a
 * different salon/login starts its own cursor; clearing the cache just resets to step one, no harm).
 *
 * We deliberately do NOT persist field values here — every step writes through to the API as it goes
 * (StaffModal, ServiceModal, salon PATCH, working-hours PUT), so the real record is always the
 * source of truth and there is nothing half-saved to lose.
 */

interface Progress {
  /** The step the operator was last on. */
  current: StepId;
  /** Steps the operator has explicitly advanced past (for the "visited" tick + jump-back). */
  visited: StepId[];
}

const KEY_PREFIX = 'slm.onboarding.';

function keyFor(salonId: string | undefined): string {
  return `${KEY_PREFIX}${salonId ?? 'unknown'}`;
}

function read(salonId: string | undefined): Progress {
  const fallback: Progress = { current: STEP_ORDER[0]!, visited: [] };
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(keyFor(salonId));
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<Progress>;
    const current =
      parsed.current && STEP_ORDER.includes(parsed.current) ? parsed.current : STEP_ORDER[0]!;
    const visited = Array.isArray(parsed.visited)
      ? parsed.visited.filter((s): s is StepId => STEP_ORDER.includes(s as StepId))
      : [];
    return { current, visited };
  } catch {
    return fallback;
  }
}

function write(salonId: string | undefined, value: Progress): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(keyFor(salonId), JSON.stringify(value));
  } catch {
    // Private-mode / quota — the wizard still works, it just won't remember the cursor.
  }
}

export interface WizardProgress {
  current: StepId;
  visited: ReadonlySet<StepId>;
  /** Jump to a step (also marks every earlier step visited, so the timeline reads correctly). */
  goTo: (step: StepId) => void;
  /** Mark the current step visited and advance to the next; no-op past the last step. */
  next: () => void;
  /** Step back to the previous step; no-op at the first step. */
  back: () => void;
}

/** Resumable wizard cursor for one salon. Reads once, persists every change. */
export function useWizardProgress(salonId: string | undefined): WizardProgress {
  const [state, setState] = useState<Progress>(() => read(salonId));

  // Re-seed if the active salon changes mid-session (salon switcher) — each salon has its own cursor.
  useEffect(() => {
    setState(read(salonId));
  }, [salonId]);

  const persist = useCallback(
    (updater: (prev: Progress) => Progress) => {
      setState((prev) => {
        const nextState = updater(prev);
        write(salonId, nextState);
        return nextState;
      });
    },
    [salonId],
  );

  const goTo = useCallback(
    (step: StepId) => {
      const idx = STEP_ORDER.indexOf(step);
      persist((prev) => ({
        current: step,
        visited: dedupe([...prev.visited, ...STEP_ORDER.slice(0, idx)]),
      }));
    },
    [persist],
  );

  const next = useCallback(() => {
    persist((prev) => {
      const idx = STEP_ORDER.indexOf(prev.current);
      const target = STEP_ORDER[Math.min(idx + 1, STEP_ORDER.length - 1)]!;
      return { current: target, visited: dedupe([...prev.visited, prev.current]) };
    });
  }, [persist]);

  const back = useCallback(() => {
    persist((prev) => {
      const idx = STEP_ORDER.indexOf(prev.current);
      const target = STEP_ORDER[Math.max(idx - 1, 0)]!;
      return { ...prev, current: target };
    });
  }, [persist]);

  return { current: state.current, visited: new Set(state.visited), goTo, next, back };
}

function dedupe(steps: StepId[]): StepId[] {
  return [...new Set(steps)];
}
